import React, { useMemo, useState } from 'react'
import { Stack, useTheme } from '@mui/material'

import LoaderMap from 'components/LoaderMap'
import { MainContent, MapOnly, SideContent, WrapperMapAccount } from 'pages/PagesStyles'
import LoaderSitesMetrics from 'components/Cartography/LoaderMetrics'
import useFocus from 'components/AuthenticatedLayout/Header/AccountsPerimeter/use-focus'
import GoogleMap from 'components/Cartography'
import KPIsAccount, { KpisInformations } from './KPIsAccount'
import GoogleMapInternal from './GoogleMapInternal'
import { useSites, useUser, countUserSites, ACCOUNTS_LIMIT } from 'store'
import { useNavigate } from 'router'

import ActiveUsers from './ActiveUsers'

const Home = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [user] = useUser()
  const [{ selected}] = useSites()
  const {onFocus} = useFocus()
  const [isMapExpanded, setIsMapExpanded] = useState(false)
  const isInternalView = useMemo(() => {
    return user.internal && selected.length === 0
  }, [user, selected])

  const canShowActiveUsers = useMemo(() => {
    if (isInternalView) {
      return false
    }
    const totalSites = countUserSites(user)
    return selected.length === 1 || totalSites < ACCOUNTS_LIMIT
  }, [user, selected, isInternalView])

  const handleMarkerClick = (sites, marker) => {
    if (!user.admin && !user.roles.supervision) {
      return
    }
    const focus = sites.find(site =>
      site.sf_id === marker.sf_id &&
      (!marker.buildingId || site.buildingId === marker.buildingId)
    )
    const targetPath = focus.isSurfaceArea
      ? `/monitoring/map/${focus.sf_id}/redirect_first_floor/${focus.buildingId}`
      : `/monitoring/map/${focus.sf_id}/redirect_first_floor`
    onFocus(focus)
    navigate({ pathname: targetPath })
  }

  const handleToggleExpand = () => {
    setIsMapExpanded(!isMapExpanded)
  }

  return (
    <Stack gap={1} flex={1} sx={{ height: '100%', minHeight: 0, overflow: 'visible' }}>
      <MainContent
        isexpanded={isMapExpanded ? 1 : 0}
        sx={{
          flex: '1 1 0',
          boxSizing: 'border-box',
          height: isMapExpanded ? '100%' : 'auto',
          minHeight: 0,
          paddingTop: isMapExpanded ? 0 : 1,
          paddingBottom: isMapExpanded ? 0 : 2,
          overflow: isMapExpanded ? 'hidden' : 'visible'
        }}>
        <SideContent
          isexpanded={isMapExpanded ? 1 : 0}
          sx={{
            height: '100%',
            paddingBottom: 2,
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
          <KPIsAccount />
        </SideContent>

        <Stack
          sx={{
            flex: 1,
            gap: isMapExpanded ? 0 : theme.spacing(3),
            width: {
              md: '480px',
              sm: '100%',
              xs: '100%'
            },
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            transition: 'gap 0.3s ease-in-out',
            [theme.breakpoints.down('md')]: {
              order: 3
            }
          }}>
          <Stack
            sx={{
              flex: 1,
              gap: isMapExpanded ? 0 : theme.spacing(3),
              height: '100%',
              minHeight: 0,
              overflow: 'hidden',
              paddingBottom: !isMapExpanded && (canShowActiveUsers || isInternalView) ? 4 : 0,
              transition: 'gap 0.3s ease-in-out, padding-bottom 0.3s ease-in-out',
              [theme.breakpoints.up('lg')]: {
                display: 'flex',
                flexDirection: 'column'
              }
            }}>
            <WrapperMapAccount
              sx={{
                flex: isMapExpanded ? '1 1 100%' : undefined,
                minHeight: isMapExpanded ? 0 : undefined,
                transition: 'flex-basis 0.3s ease-in-out, flex-grow 0.3s ease-in-out, min-height 0.3s ease-in-out',
                [theme.breakpoints.up('lg')]: {
                  flex: isMapExpanded
                    ? '1 1 100%'
                    : canShowActiveUsers || isInternalView
                      ? '0 0 60%'
                      : '1',
                  minHeight: 0
                }
              }}>
              <MapOnly isexpanded={isMapExpanded ? 1 : 0}>
                <Stack
                  sx={{
                    flexGrow: 1,
                    justifyContent: 'center',
                    borderRadius: 2,
                    height: '100%',
                    minHeight: !isMapExpanded && (canShowActiveUsers || isInternalView)
                      ? {
                          xs: theme.spacing(25),
                          sm: theme.spacing(30),
                          md: theme.spacing(40),
                          lg: 0
                        }
                      : 0,
                    width: '100%',
                    transition: 'min-height 0.3s ease-in-out'
                  }}>
                  <LoaderSitesMetrics loader={<LoaderMap />}>
                    {({ sites }) =>
                      isInternalView ? (
                        <GoogleMapInternal
                          onToggleExpand={handleToggleExpand}
                          isExpanded={isMapExpanded}
                        />
                      ) : (
                        <GoogleMap
                          sites={sites}
                          onMarkerClick={marker => handleMarkerClick(sites, marker)}
                          isExpanded={isMapExpanded}
                          onToggleExpand={handleToggleExpand}
                          showExpandButton={true}
                        />
                      )
                    }
                  </LoaderSitesMetrics>
                </Stack>
              </MapOnly>
            </WrapperMapAccount>
            {isInternalView && (
              <Stack
                sx={{
                  flex: isMapExpanded ? '0 0 0' : '0 0 auto',
                  maxHeight: isMapExpanded ? 0 : 'none',
                  overflow: 'hidden',
                  opacity: isMapExpanded ? 0 : 1,
                  transition: 'flex-basis 0.3s ease-in-out, max-height 0.3s ease-in-out, opacity 0.2s ease-in-out',
                  [theme.breakpoints.up('lg')]: {
                    flex: isMapExpanded ? '0 0 0' : '0 0 40%',
                    minHeight: 0
                  }
                }}>
                <KpisInformations />
              </Stack>
            )}
            {canShowActiveUsers && (
              <Stack
                sx={{
                  flex: isMapExpanded ? '0 0 0' : '0 0 auto',
                  maxHeight: isMapExpanded ? 0 : 'none',
                  overflow: 'hidden',
                  opacity: isMapExpanded ? 0 : 1,
                  transition: 'flex-basis 0.3s ease-in-out, max-height 0.3s ease-in-out, opacity 0.2s ease-in-out',
                  [theme.breakpoints.up('lg')]: {
                    flex: isMapExpanded ? '0 0 0' : '0 0 40%',
                    minHeight: 0
                  }
                }}>
                <ActiveUsers />
              </Stack>
            )}
          </Stack>
        </Stack>
      </MainContent>
    </Stack>
  )
}

export default Home
